import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ReactTable } from "../../Molecules";
import { VIEW_VEHICLES_HEADERS } from "../../../Library/tableHeaders";

import "./style.css";
import { getFilters } from "../../../Library/tableHelpers";
import { Link } from "react-router-dom";
import PATHS from "../../../Routes/paths";
import { getItemFromLocalStorage } from "../../../Library/helpers";
import { getLocalisedDate, getLocalisedTime } from "../../../Library/datetime";

const VehiclesList = ({ allVehicles, tableHeaders, vehicleStats, highlightedVehicle }) => {
  const user = useRef(getItemFromLocalStorage());
  const vehStatsRef = useRef(vehicleStats);
  const [ forceRerender, setForceRerender ] = useState(0);

  const staticColumns = useMemo(() => Object.values(tableHeaders || VIEW_VEHICLES_HEADERS)
    .filter(header => !["ignition", "spd", "status", "ts"].includes(header.key))
    .map(header => ({
      Header: header.nm,
      accessor: header.key,
      disableSortBy: !header.sorting,
      Cell: (row) => {
        if (header.navigate) {
          let navigate = `${PATHS.LEASED_VEHICLES}/${PATHS.VEHICLE_STATS}?idx=${row.row.original.idx}`;
          if (user.current) navigate = `${PATHS.LIVE_TRACKING}/${PATHS.VEHICLE_STATS}?idx=${row.row.original.idx}&lt=true`;
          return <Link className="text-blue" to={navigate}>{row.value}</Link>;
        }

        if (header.key === "bId") {
          return <Link className="text-blue" to={`${PATHS.BATTERIES}/${row.value}`}>{row.value}</Link>;
        }

        return row.value || "-";
      },
      ...getFilters(header),
    })), [ tableHeaders ]);

    const handleSorting = (r1, r2, header) => {
      const veh1Id = r1.original.idx;
      const veh2Id = r2.original.idx;
      const veh1Stats = vehStatsRef.current.get(veh1Id) || { spd: 0, ts: 0, ignition: 0 };
      const veh2Stats = vehStatsRef.current.get(veh2Id) || { spd: 0, ts: 0, ignition: 0 };

      if (header === "ts") return veh1Stats.ts - veh2Stats.ts;
      if (header === "ignition") return veh1Stats.ignition - veh2Stats.ignition;
      if (header === "spd") {
        const veh1Speed = (veh1Stats.ignition && veh1Stats.spd) ? veh1Stats.spd : 0;
        const veh2Speed = (veh2Stats.ignition && veh2Stats.spd) ? veh2Stats.spd : 0;
        return veh1Speed - veh2Speed;
      }
    };

    const tsWithin10Mins = (ts) => {
      let newTs = ts * 1000;
      const now = Date.now();
      const tenMinsAgo = now - (10 * 60 * 1000);
      return ((newTs >= tenMinsAgo) && (newTs <= now));
    };

    const getCellRenderVal = (row, header) => {
      const vehicle = vehStatsRef.current.get(row.row.original.idx);
      row.row.original.ping = vehicle;

      if (header.key === "ignition") {
        return vehicle ? (vehicle.ignition ? "ON" : "OFF") : "-";
      }

      if (header.key === "spd") {
        return vehicle ? ((vehicle.ignition && tsWithin10Mins(vehicle.ts)) ? (vehicle.spd && vehicle.spd.toFixed(0)) : 0) : "-";
      }

      if (header.key === "status") {
        if (vehicle) {
          if (!vehicle.mainPower && ((vehicle.temp && !vehicle.heartbeat) || (vehicle.heartbeat && vehicle.heartbeat.mainPower === false))) return "Disconnected";
          if (vehicle.ignition && (vehicle.ts && tsWithin10Mins(vehicle.ts)) && vehicle.spd > 5) return "Running";
          return "Idle";
        } else {
          return "-";
        }
      }

      if (header.key === "ts") {
        return vehicle ? `${getLocalisedDate(vehicle.ts * 1000, true)} ${getLocalisedTime(vehicle.ts * 1000, true, null, true)}` : "-";
      }

      return row.value || "-";
    }


  let dynamicColumns = useMemo(() => Object.values(tableHeaders || VIEW_VEHICLES_HEADERS)
    .filter(header => ["ignition", "spd", "status", "ts"].includes(header.key))
    .map(header => ({
        Header: header.nm,
        accessor: header.key,
        disableSortBy: !header.sorting,
        Cell: null,
        filter: (rows, _, filterValue) => {
          return rows.filter((row) => {
            const rowValue = `${getCellRenderVal({row: row}, { key: header.key })}`;
            return rowValue.toLowerCase().includes(filterValue.toLowerCase())
          });
        }
      })), [ tableHeaders ]);

  useEffect(() => {
    vehStatsRef.current = vehicleStats;
    if (vehStatsRef.current && vehStatsRef.current.size > 1 && forceRerender < 1) setForceRerender(1);
  }, [vehicleStats]);

  dynamicColumns.forEach(column => {
    column.Cell = (row) => getCellRenderVal(row, { key: row.column.id });
    column.sortType = (r1, r2, _) => handleSorting(r1, r2, column.accessor);
  });

  const columns = useMemo(() => [...staticColumns, ...dynamicColumns], [staticColumns, dynamicColumns]);

  return (
    <div className={highlightedVehicle && highlightedVehicle.idx ? "highlight-vehicle" : ""}>
      <ReactTable
        key={highlightedVehicle ? highlightedVehicle.idx : "normal"}
        columns={columns}
        data={allVehicles}
        showSearch={true}
        showOverlay={false}
        heading="Vehicles"
        pageSize={20}
      />
    </div>
  );
};

export default VehiclesList;